import { PICKER_ETHNICITY } from 'constants/picker';
import { phoneNumberRegex } from 'utils/regex';

export type SignUpPrefill = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  school?: string;
  grade?: string;
  ethnicity?: string;
  ethnicityOther?: string;
};

const GRADE_VALUES = new Set([
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  'freshman',
  'sophomore',
  'junior',
  'senior',
  'graduated',
  'Not in school',
  'professional',
]);

function firstParam(params: URLSearchParams, keys: string[]): string {
  for (let i = 0; i < keys.length; i += 1) {
    const value = params.get(keys[i]);
    if (value && value.trim()) return value.trim().slice(0, 200);
  }
  return '';
}

function formatPhone(raw: string): string {
  if (phoneNumberRegex.test(raw)) return raw;
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return '';
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

function normalizeGrade(raw: string): string {
  const compact = raw.trim();
  if (GRADE_VALUES.has(compact)) return compact;
  const lower = compact.toLowerCase();
  const named = Array.from(GRADE_VALUES).find(value => value.toLowerCase() === lower);
  if (named) return named;
  const leading = lower.match(/^(\d{1,2})/);
  if (leading && GRADE_VALUES.has(leading[1])) return leading[1];
  return '';
}

function normalizeEthnicity(raw: string): string {
  const match = PICKER_ETHNICITY.find(
    option => option.value.toLowerCase() === raw.toLowerCase()
      || option.label.toLowerCase() === raw.toLowerCase(),
  );
  return match ? match.value : '';
}

/**
 * Reads recruiting-link query params. Unknown keys and passwords are ignored.
 * Also accepts the names the interest email currently sends (`schoolName`, `gradeLevel`).
 */
export function readSignUpPrefill(params: URLSearchParams): SignUpPrefill {
  const prefill: SignUpPrefill = {};

  const firstName = firstParam(params, ['firstName']);
  const lastName = firstParam(params, ['lastName']);
  const email = firstParam(params, ['email']);
  const phoneNumber = formatPhone(firstParam(params, ['phoneNumber']));
  const school = firstParam(params, ['school', 'schoolName']);
  const grade = normalizeGrade(firstParam(params, ['grade', 'gradeLevel']));
  const ethnicity = normalizeEthnicity(firstParam(params, ['ethnicity']));
  const ethnicityOther = firstParam(params, ['ethnicityOther']);

  if (firstName) prefill.firstName = firstName.slice(0, 50);
  if (lastName) prefill.lastName = lastName.slice(0, 50);
  if (email) prefill.email = email;
  if (phoneNumber) prefill.phoneNumber = phoneNumber;
  if (school) prefill.school = school;
  if (grade) prefill.grade = grade;
  if (ethnicity) prefill.ethnicity = ethnicity;
  if (ethnicity === 'other' && ethnicityOther) {
    prefill.ethnicityOther = ethnicityOther;
  }

  return prefill;
}

export function hasSignUpPrefill(prefill: SignUpPrefill): boolean {
  return Object.keys(prefill).length > 0;
}
